import { useEffect, useState } from "react";

/**
 * The reveal plays as one short film, not a layout.
 *
 *   3 · 2 · 1     both options still up, the room still guessing at nothing
 *   fade          the option nobody picked goes dark
 *   rise          the picked option travels to the top and takes the frame
 *   author        whoever wrote it is named, over their own words
 *   summary       your result, then the room split into right and wrong
 *
 * Stages are **additive** — each adds a fact and keeps the ones before it — so
 * looking up mid-sequence shows a partial truth, never a wrong one, and
 * `Summary` is the complete picture standing alone.
 *
 * That last property is what makes the reduced-motion path honest: the global
 * rule in `index.css` collapses durations to ~0ms, so a transition-driven
 * sequence would flash to its end and lose every beat. Here the sequence is
 * skipped outright and the final composition renders directly.
 *
 * ~4.6s against a 30-second reaction window. The host may continue once the
 * complete picture is visible.
 */
export enum RevealStage {
  Countdown = 0,
  Fade = 1,
  Rise = 2,
  Author = 3,
  Summary = 4,
}

const COUNT_FROM = 3;
const TICK_MS = 1_000;

/** Offsets from the end of the countdown. */
const AFTER_COUNTDOWN_MS: Record<Exclude<RevealStage, RevealStage.Countdown>, number> = {
  [RevealStage.Fade]: 0,
  [RevealStage.Rise]: 500,
  [RevealStage.Author]: 1_100,
  [RevealStage.Summary]: 1_600,
};

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

export interface RevealSequence {
  stage: RevealStage;
  /** 3, 2, 1 — then 0 once the countdown is spent. */
  count: number;
}

/**
 * `key` restarts the sequence; pass something replaced per turn. The store
 * swaps the whole `TurnRevealed` payload, so its object identity is enough.
 */
export const useRevealSequence = (key: unknown): RevealSequence => {
  // The first paint must already be the two-option countdown composition.
  // Starting at Summary and correcting it in the effect makes the stage grow
  // back to full height after mount, which reads as a layout shift.
  const [seq, setSeq] = useState<RevealSequence>(() =>
    prefersReducedMotion()
      ? { stage: RevealStage.Summary, count: 0 }
      : { stage: RevealStage.Countdown, count: COUNT_FROM },
  );

  useEffect(() => {
    if (prefersReducedMotion()) {
      setSeq({ stage: RevealStage.Summary, count: 0 });
      return;
    }

    setSeq({ stage: RevealStage.Countdown, count: COUNT_FROM });

    const timers: Array<ReturnType<typeof setTimeout>> = [];

    for (let n = COUNT_FROM - 1; n >= 1; n--) {
      timers.push(
        setTimeout(
          () => setSeq({ stage: RevealStage.Countdown, count: n }),
          (COUNT_FROM - n) * TICK_MS,
        ),
      );
    }

    const countdownEnd = COUNT_FROM * TICK_MS;
    for (const [stage, offset] of Object.entries(AFTER_COUNTDOWN_MS)) {
      timers.push(
        setTimeout(
          () => setSeq({ stage: Number(stage) as RevealStage, count: 0 }),
          countdownEnd + offset,
        ),
      );
    }

    return () => timers.forEach(clearTimeout);
  }, [key]);

  return seq;
};
